import Link, {
  Button,
  ButtonList,
  StickyButtons,
  LargeViewOnly,
  ButtonDualGroup,
  ButtonGroup,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import Tags from "../../../components/tags";
import ImageSet from "../../../components/image-set";
import Image from "../../../components/image";
import Author from "../../../components/author";
import EcoFlowCouponCode from "../components/coupon-code";
import NoWrap from "../../../components/no-wrap";
import SlugHeading from "../../../components/slug-heading";
import ReviewItem from "../../../components/review-item";
import namedRoutes from "../../../settings/routes";
import { lookupByUrl } from "../../../settings/route-data-lookup";

const headerInfo = lookupByUrl(namedRoutes.ecoflow.altCharger.quick);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      short
      positionY={67}
      background="/images/r/ecoflow-alternator-chargers/ecoflow-car.webp"
      titlePrefix="EcoFlow"
      title="Alternator Chargers"
      subTitle="500W and 800W Quick Review"
    />
    <Editorial>
      <Center>
        <Author
          preText="Quick read: ~2 minutes"
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          EcoFlow’s <strong>500W Alternator Charger</strong> and{" "}
          <strong>800W Alternator Charger</strong> are designed to keep your
          power stations topped up while you drive, providing a convenient
          off-grid power solution. They draw power from your vehicle’s
          alternator, ensuring your portable power station is ready when you
          arrive at your destination.
        </p>

        <LargeViewOnly>
          <ButtonDualGroup small>
            <ButtonGroup title="800W Alternator Charger">
              <ButtonList size="medium">
                <Button
                  size="medium"
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://us.ecoflow.com/products/800w-alternator-charger"
                  icon="EcoFlow"
                >
                  View on EcoFlow
                </Button>
                <Button
                  size="medium"
                  affiliate="https://amzn.to/3N4b6p1"
                  href="https://www.amazon.com/dp/B0DSBZVPS6/"
                  secondary
                  icon="Amazon"
                >
                  View on Amazon
                </Button>
              </ButtonList>
            </ButtonGroup>
            <ButtonGroup title="500W Alternator Charger">
              <ButtonList size="medium">
                <Button
                  size="medium"
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://us.ecoflow.com/products/ecoflow-500w-alternator-charger"
                  icon="EcoFlow"
                >
                  View on EcoFlow
                </Button>
                <Button
                  size="medium"
                  affiliate="https://amzn.to/4jzlwsP"
                  href="https://www.amazon.com/dp/B0FYCJ6KD3/"
                  secondary
                  icon="Amazon"
                >
                  View on Amazon
                </Button>
              </ButtonList>
            </ButtonGroup>
          </ButtonDualGroup>
        </LargeViewOnly>

        <AffiliateDisclosure />
      </Center>
      <Center>
        <SlugHeading tag="h2">Key Differences</SlugHeading>
        <p>
          The main differences between the two models is the included cable and
          max power output:
        </p>
        <ul>
          <li>
            <strong>Output Cable:</strong> 500W includes XT60 cable, 800W
            includes XT150 cable.
          </li>
          <li>
            <strong>Features:</strong> Output cable changes what features are
            enabled and maximum power output.
          </li>
          <li>
            <strong>Compatibility:</strong> Both models are compatible with
            either output cable.
          </li>
          <li>
            <strong>Appearance:</strong> Almost visually identical except for
            cabling.
          </li>
          <li>
            <strong>Weight:</strong> 1596g (3.5lbs) vs 2230g (4.9lbs) for main
            unit.
          </li>
        </ul>
        <ImageSet
          shadow={true}
          images={[
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250505_164631426.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250505_164631426.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250505_164631426.webp",
              alt: "800W vs 500W base units top view",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250505_164837918.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250505_164837918.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250505_164837918.webp",
              alt: "800W vs 500W base units side view",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250505_164245603.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250505_164245603.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250505_164245603.webp",
              alt: "XT150 cable vs XT60 cable",
            },
            {
              max: "/images/r/ecoflow-alternator-chargers/max/20250505_184511737.jpg",
              lg: "/images/r/ecoflow-alternator-chargers/lg/20250505_184511737.webp",
              sm: "/images/r/ecoflow-alternator-chargers/sm/20250505_184511737.webp",
              alt: "Fuse blocks and 6AWG vs 8AWG cables",
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Output Cables</SlugHeading>
        <SlugHeading tag="h3">XT150 Cable</SlugHeading>
        <Image
          right
          src="/images/products/ecoflow/alternator-charger-800w/output-cable-xt150-2m.webp"
          alt="XT150 Output Cable"
          showAlt
        />
        <p>
          With the XT150 output cable, you gain access to all features
          including:
        </p>
        <ul>
          <li>
            <strong>Adjustable charge rate:</strong> 100-500W/100-800W depending
            on model.
          </li>
          <li>
            <strong>Reverse charging:</strong> Charge your vehicle’s battery
            from your EcoFlow in an emergency.
          </li>
          <li>
            <strong>Emergency reverse charging:</strong> For deeply depleted
            batteries.
          </li>
          <li>
            <strong>Battery maintenance mode:</strong> Keep your vehicle’s
            battery healthy when stored.
          </li>
          <li>
            <strong>Adapter:</strong> May require the{" "}
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter"
            >
              Smart Generator Adapter
            </Link>{" "}
            to use with some power stations or purchase the{" "}
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-alternator-charger-custom-fit-output-cable-2m"
            >
              Custom Fit Output Cable
            </Link>{" "}
            which offers the same features.
          </li>
        </ul>

        <SlugHeading tag="h3">XT60 Cable</SlugHeading>
        <Image
          right
          src="/images/products/ecoflow/alternator-charger-500w/output-cable-xt60i.webp"
          alt="XT60 Output Cable"
          showAlt
        />
        <p>
          The XT60 output cable restricts functionality and maximum charge rate:
        </p>
        <ul>
          {/* <li>
            <strong>Auto shut-off issue:</strong> A significant drawback. The
            charger shuts off after 30 seconds of inactivity, requiring you to
            manually turn it on each time. Smart alternators (which adjust
            voltage output for fuel efficiency) can cause it to shut off while
            you’re driving.
          </li> */}
          <li>
            <strong>Highly compatible:</strong> Compatible with 3rd party power
            stations and EcoFlow's River series.
          </li>
          <li>
            <strong>Fixed 500W charge rate:</strong> No adjusting the amperage.
            Voltage can be adjusted for 3rd-party devices.
          </li>
          <li>
            <strong>No reverse charging.</strong>
          </li>
          <li>
            <strong>No battery maintenance mode.</strong>
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">New Improvements</SlugHeading>
        <ul>
          {/* <li>
            <strong>Supports River and 3rd party power stations:</strong> The
            XT60 cable enables compatibility with other power station brands and
            EcoFlow’s River series.
          </li> */}
          <li>
            <strong>Resolved timeout issue:</strong> EcoFlow released a new
            firmware version (3.0.4.13) which resolves a timeout issue with the
            XT60 cable by adding a configurable timeout and battery protection
            feature for both cable types.
          </li>
          <li>
            <strong>Improved fuse block:</strong> EcoFlow addressed a previous
            design flaw in the fuse block, increasing reliability. (I’m awaiting
            confirmation from EcoFlow if this improved design is also included
            with the 800W model)
          </li>
          <li>
            <strong>Positive electrode extension cable:</strong> Added support
            for more install configurations on both models. Use your own cable
            or purchase EcoFlow’s{" "}
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-alternator-charger-extended-fuse-cable-2m"
            >
              Extended Fuse Cable (2m)
            </Link>
            .
          </li>
          <li>
            <strong>Output cables available for purchase separately:</strong>
            <ul>
              <li>
                <Link
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://us.ecoflow.com/products/ecoflow-alternator-charger-xt60-output-cable-1m"
                >
                  XT60 Output Cable
                </Link>
              </li>
              <li>
                <Link
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://us.ecoflow.com/products/aecoflow-alternator-charger-xt150-output-cable-2m"
                >
                  XT150 Output Cable
                </Link>
              </li>
              <li>
                <Link
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://us.ecoflow.com/products/ecoflow-alternator-charger-custom-fit-output-cable-2m"
                >
                  Custom Fit Output Cable
                </Link>
              </li>
            </ul>
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Power Station Compatibility</SlugHeading>
        <p>
          Find complete compatibility tables, cable limits, and a 3rd‑party
          power station compatibility calculator in my detailed compatibility
          guide:
        </p>
        <ReviewItem href={namedRoutes.ecoflow.altCharger.gen1Compatibility} />
        <p>
          <strong>Unsure which port your EcoFlow uses?</strong> My cable & port
          guide lists every model’s port type, port count, cable options,
          adapters, and charging limits.
        </p>
        <ReviewItem href={namedRoutes.ecoflow.altCharger.gen1CableGuide} />
      </Center>
      <Center>
        <SlugHeading tag="h2">The Verdict</SlugHeading>
        <p>
          Your choice depends heavily on the cable and your power station’s
          compatibility:
        </p>
        <ul>
          <li>
            <strong>
              If you have an unused XT150 port on your EcoFlow power station:
            </strong>{" "}
            Go with the 800W model for the extra power headroom and access to
            all features.
          </li>
          <li>
            <strong>If you need to use an XT60 port:</strong> Consider the 500W
            model but understand you’re missing out on some functionality.
          </li>
        </ul>

        <p>
          EcoFlow’s Alternator Chargers offer a compelling way to charge your
          power stations on the go. Many more details are available in my{" "}
          <Link href={namedRoutes.ecoflow.altCharger.review}>
            full detailed review
          </Link>
          . Do note that both models require an iOS or Android app to configure
          settings.
        </p>

        <EcoFlowCouponCode
          href="https://us.ecoflow.com/products/800w-alternator-charger"
          buttonText="Buy from EcoFlow"
        />

        <p>
          Not sure it’s the best fit? My{" "}
          <Link href={namedRoutes.alternatorChargers}>
            alternator charger guide
          </Link>{" "}
          covers all the popular brands.
        </p>

        <ButtonDualGroup>
          <ButtonGroup title="800W Alternator Charger">
            <ButtonList>
              <Button
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/800w-alternator-charger"
                icon="EcoFlow"
              >
                Buy from EcoFlow
              </Button>
              <Button
                affiliate="https://amzn.to/3N4b6p1"
                href="https://www.amazon.com/dp/B0DSBZVPS6/"
                secondary
                icon="Amazon"
              >
                Buy on Amazon
              </Button>
            </ButtonList>
          </ButtonGroup>
          <ButtonGroup title="500W Alternator Charger">
            <ButtonList>
              <Button
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/ecoflow-500w-alternator-charger"
                icon="EcoFlow"
              >
                Buy from EcoFlow
              </Button>
              <Button
                affiliate="https://amzn.to/4jzlwsP"
                href="https://www.amazon.com/dp/B0FYCJ6KD3/"
                secondary
                icon="Amazon"
              >
                Buy on Amazon
              </Button>
            </ButtonList>
          </ButtonGroup>
        </ButtonDualGroup>

        <p style="margin-top: 2em;">
          <strong>Want more details, including lots of photos?</strong>
        </p>
        <ReviewItem href={namedRoutes.ecoflow.altCharger.review} />

        <p style="margin-top: 2em;">
          <strong>Need an install guide for a Ford Transit? </strong>
        </p>
        <ReviewItem href={namedRoutes.ecoflow.altCharger.transit} />

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.gen2} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.wiring12v} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="EcoFlow Alternator Chargers"
        description="800W and 500W"
        options={[
          {
            text: "View on EcoFlow",
            icon: "EcoFlow",
            options: [
              {
                text: "800W Alternator Charger",
                affiliateData: affiliateMerchants.ecoflow,
                href: "https://us.ecoflow.com/products/800w-alternator-charger",
              },
              {
                text: "500W Alternator Charger",
                affiliateData: affiliateMerchants.ecoflow,
                href: "https://us.ecoflow.com/products/ecoflow-500w-alternator-charger",
              },
            ],
          },
          {
            text: "View on Amazon",
            icon: "Amazon",
            secondary: true,
            options: [
              {
                text: "800W Alternator Charger",
                affiliate: "https://amzn.to/3N4b6p1",
                href: "https://www.amazon.com/dp/B0DSBZVPS6/",
              },
              {
                text: "500W Alternator Charger",
                affiliate: "https://amzn.to/4jzlwsP",
                href: "https://www.amazon.com/dp/B0FYCJ6KD3/",
              },
            ],
          },
        ]}
      />
    </Editorial>
  </div>
);

export default Page;
